import { useEffect, useRef, useState } from "react";
import { Bot, X, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = ["Book a test", "Home sample collection", "Health packages", "Contact details"];

const GREETING =
  "Namaste! I'm the AI assistant for Dr Lal PathLabs Collection Center, Bhopal. Ask me about tests, health packages, booking or home sample collection.";

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionId = useRef(crypto.randomUUID());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);

    const append = (chunk) =>
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
        return copy;
      });

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const data = part.slice(6).trim();
          if (!data || data === "[DONE]") continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.content) append(parsed.content);
            else if (parsed.error) append(parsed.error);
          } catch {
            /* ignore malformed chunk */
          }
        }
      }
    } catch {
      append("Sorry, I couldn't connect right now. Please call 8959830932 or message us on WhatsApp.");
    }
    setBusy(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid="chat-widget-button"
        aria-label={open ? "Close AI chat assistant" : "Open AI chat assistant"}
        className="fixed bottom-24 left-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#0B3056] text-white shadow-xl transition-transform duration-200 hover:scale-110 md:bottom-8 md:left-6"
      >
        {open ? <X size={24} aria-hidden="true" /> : <Bot size={26} aria-hidden="true" />}
      </button>

      {open && (
        <div
          className="fixed bottom-[168px] left-4 right-4 z-50 flex max-h-[60vh] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl sm:left-5 sm:right-auto sm:w-[380px] md:bottom-24 md:left-6"
          data-testid="chat-panel"
          role="dialog"
          aria-label="AI chat assistant"
        >
          <div className="bg-[#0B3056] px-5 py-4">
            <p className="font-heading text-sm font-bold text-white">AI Assistant</p>
            <p className="text-xs text-blue-200">Test &amp; booking info — not medical advice</p>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-[#F0F7FA] px-4 py-4" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "rounded-br-sm bg-[#1E88E5] text-white"
                      : "rounded-bl-sm border border-gray-100 bg-white text-slate-700 shadow-sm"
                  }`}
                >
                  {m.content || (busy && i === messages.length - 1 ? <span className="inline-block animate-pulse text-slate-400">Typing...</span> : null)}
                </div>
              </div>
            ))}
          </div>

          {messages.length <= 1 && (
            <div className="flex flex-wrap gap-2 border-t border-gray-100 bg-white px-4 py-3">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => send(s)}
                  data-testid={`chat-suggestion-${s.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-[#1E88E5] hover:text-[#1E88E5]"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="flex items-center gap-2 border-t border-gray-100 bg-white px-3 py-3"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about tests, booking..."
              aria-label="Type your message"
              data-testid="chat-input"
              maxLength={1000}
              className="min-h-[44px] flex-1 rounded-full border border-gray-200 px-4 text-sm focus:border-[#1E88E5] focus:outline-none focus:ring-2 focus:ring-[#1E88E5]/30"
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              data-testid="chat-send-button"
              aria-label="Send message"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#1E88E5] text-white transition-colors hover:bg-[#1565C0] disabled:opacity-50"
            >
              <Send size={18} aria-hidden="true" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
